import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  useTheme,
  useMediaQuery,
  Tooltip,
  Button,
  Backdrop,
  Paper,
  Fade,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
} from "@mui/material";
import apiClient from "../services/api";
import { openRazorpayCheckout } from "../services/paymentsApi";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  AccountBalanceWallet as WalletIcon,
  Receipt as ReceiptIcon,
  ReceiptLong as ReceiptLongIcon,
  Visibility as VisibilityIcon,
  LocalOffer as CouponIcon,
  Work as WorkIcon,
  Store as StoreIcon,
  BusinessCenter as BusinessCenterIcon,
  Settings as SettingsIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  Computer as ComputerIcon,
  Person as PersonIcon,
  Campaign as AdsIcon,
  PendingActions as PendingActionsIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Forum as ForumIcon,
  Search as SearchIcon,
  CloudDownload as CloudDownloadIcon,
  VerifiedUser as VerifiedUserIcon,
  SmsFailed as SmsFailedIcon,
  CalendarMonth as CalendarMonthIcon,
  AutoAwesome as AutoAwesomeIcon,
  PersonAdd as PersonAddIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Home as HomeIcon,
  LocalLibrary as LibraryIcon,
  DashboardCustomize as DashboardCustomizeIcon,
  TableChart as TableChartIcon,
  History as HistoryIcon,
  WhatsApp as WhatsAppIcon,
  Assessment as AssessmentIcon,
  School as SchoolIcon,
  PhoneInTalk as PhoneInTalkIcon,
  ShoppingCartCheckout as ShoppingCartCheckoutIcon,
  HighQuality as HighQualityIcon,
  Description as DescriptionIcon,
  PhoneAndroid as PhoneAndroidIcon,
  GridView as GridViewIcon,
  Image as ImageIcon,
  FeedbackOutlined as FeedbackOutlinedIcon,
  Tune as TuneIcon,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { expandLegacyOperatorPermissions } from "../utils/expandLegacyOperatorPermissions";
import DatabaseStatus from "../components/DatabaseStatus";
import { pathnameAllowedByFranchiseFeatures } from "../config/franchiseeFeatures";
import { franchiseeOperatorCanAccessPath } from "../config/franchiseeOperatorPermissions";
import { isLocalInternalHost } from "../utils/localInternalHost";
import {
  EXTRA_FIND_PAGES,
  buildPageSearchHaystack,
  mergeFindPageLists,
} from "../config/dashboardFindPages";
import {
  ADMIN_CONTROL_HUB_ROLES,
  ADMIN_CONTROL_SECTIONS,
  getAdminControlHubPaths,
  operatorHasAnyAdminControlPermission,
  userCanAccessAdminControlItem,
} from "../config/adminControlScreens";
import shopIndentsApi from "../services/shopIndentsApi";

const drawerWidth = 240;

const SHOP_ORDERS_ROLES = [
  "franchisee_admin",
  "net_center_admin",
  "franchisee_operator",
  "app_admin",
  "super_admin",
  "app_operator",
  "business_admin",
  "shop_admin",
];

const getFirstArray = (obj, paths) => {
  if (!obj) return [];
  for (const path of paths) {
    const value = obj[path];
    if (Array.isArray(value)) return value;
  }
  return [];
};

const menuItems = [
  // Priority billing shortcuts
  {
    text: "Smart Bill (QR)",
    icon: <ReceiptLongIcon />,
    path: "/bills/create",
    roles: [
      "franchisee_admin",
      "net_center_admin",
      "business_admin",
      "app_admin",
      "super_admin",
      "app_operator",
    ],
    permission: "bills_creation",
    keywords: ["smart bill", "qr bill", "bill create", "create bill"],
  },
  {
    text: "Smart Bills Drafts",
    icon: <DescriptionIcon />,
    path: "/bills/drafts",
    roles: [
      "franchisee_admin",
      "net_center_admin",
      "business_admin",
      "app_admin",
      "super_admin",
      "app_operator",
    ],
    permission: "bills_creation",
    keywords: ["smart bill drafts", "draft bills", "resume draft", "saved drafts"],
  },
  {
    text: "Cart Model Bill",
    icon: <ShoppingCartCheckoutIcon />,
    path: "/bills/cart-model",
    roles: [
      "franchisee_admin",
      "net_center_admin",
      "business_admin",
      "app_admin",
      "super_admin",
      "app_operator",
    ],
    permission: "bills_creation",
    keywords: ["cart bill", "cart model", "cart model bill", "quick bill"],
  },
  {
    text: "View Bills",
    icon: <VisibilityIcon />,
    path: "/bills/view",
    roles: [
      "franchisee_admin",
      "net_center_admin",
      "business_admin",
      "app_admin",
      "super_admin",
      "app_operator",
    ],
    permission: "bills_view",
    keywords: ["bill history", "bills history", "bill analytics", "search bills"],
  },
  {
    text: "Advance Bills",
    icon: <WalletIcon />,
    path: "/bills/advance-bills",
    roles: [
      "franchisee_admin",
      "net_center_admin",
      "app_admin",
      "super_admin",
      "app_operator",
    ],
    permission: "bills_view",
    keywords: ["advance", "advance bills", "advance amount"],
  },
  {
    text: "Pending Works",
    icon: <PendingActionsIcon />,
    path: "/bills/pending-works",
    roles: [
      "franchisee_admin",
      "net_center_admin",
      "app_admin",
      "super_admin",
      "app_operator",
    ],
    permission: "pending_works",
  },
  {
    text: "Enquiries",
    icon: <QuestionAnswerIcon />,
    path: "/bills/enquiry-form",
    roles: [
      "franchisee_admin",
      "net_center_admin",
      "app_admin",
      "super_admin",
      "app_operator",
    ],
    permission: "enquiries",
    keywords: [
      "enquiry form",
      "enquiries",
      "register enquiry",
      "callback",
      "whatsapp enquiry",
    ],
  },
  {
    text: "Operator Call Leads",
    icon: <PhoneInTalkIcon />,
    path: "/bills/operator-calls",
    roles: [
      "franchisee_admin",
      "net_center_admin",
      "app_admin",
      "super_admin",
      "app_operator",
    ],
    permission: "operator_call_leads",
  },
  {
    text: "Old Site Bills",
    icon: <HistoryIcon />,
    path: "/bills/manage",
    roles: [
      "franchisee_admin",
      "net_center_admin",
      "app_admin",
      "super_admin",
      "app_operator",
    ],
    permission: "old_site_bills",
  },
  {
    text: "Application Chat",
    icon: <ForumIcon />,
    path: "/application-chat",
    roles: [
      "app_admin",
      "super_admin",
      "app_operator",
      "net_center_admin",
      "shop_admin",
      "franchisee_admin",
    ],
    permission: "enquiries",
  },
  {
    text: "Customer History",
    icon: <SearchIcon />,
    path: "/customer/search",
    roles: [
      "franchisee_admin",
      "net_center_admin",
      "app_admin",
      "super_admin",
      "app_operator",
    ],
    permission: "customer_search",
  },
  {
    text: "Counter Stats",
    icon: <ReceiptIcon />,
    path: "/bills/service-charges",
    roles: ["franchisee_admin", "net_center_admin", "app_admin", "super_admin"],
  },
  {
    text: "Inventory",
    icon: <SettingsIcon />,
    path: "/bills/inventory",
    roles: ["app_admin", "super_admin", "franchisee_admin", "net_center_admin", "business_admin", "shop_admin"],
  },
  {
    text: "Product / Service Groups",
    icon: <SettingsIcon />,
    path: "/bills/main-categories",
    roles: ["app_admin", "super_admin", "franchisee_admin", "net_center_admin", "business_admin", "shop_admin"],
  },
  {
    text: "Products / Services",
    icon: <SettingsIcon />,
    path: "/bills/services",
    roles: ["app_admin", "super_admin", "franchisee_admin", "net_center_admin", "business_admin", "shop_admin"],
  },
  {
    text: "Admin Controls",
    icon: <SettingsIcon />,
    path: "/bills/admin-controls",
    roles: [
      "app_admin",
      "super_admin",
      "franchisee_admin",
      "net_center_admin",
      "business_admin",
    ],
  },
  {
    text: "WhatsApp Message Config",
    icon: <WhatsAppIcon />,
    path: "/bills/whatsapp-config",
    roles: [
      "app_admin",
      "super_admin",
      "franchisee_admin",
      "net_center_admin",
      "franchisee_operator",
      "app_operator",
    ],
  },
  {
    text: "Professions",
    icon: <WorkIcon />,
    path: "/professions",
    roles: ["app_admin", "super_admin", "franchisee_admin", "net_center_admin", "business_admin", "shop_admin"],
  },
  {
    text: "KNLS Services",
    icon: <VerifiedUserIcon />,
    path: "/services",
    roles: [
      "app_admin",
      "super_admin",
      "franchisee_admin",
      "net_center_admin",
      "user",
      "customer",
    ],
  },
  {
    text: "KNLS Courses",
    icon: <LibraryIcon />,
    path: "/courses",
    roles: [
      "app_admin",
      "super_admin",
      "franchisee_admin",
      "net_center_admin",
      "user",
      "customer",
    ],
  },
  {
    text: "Edit Training Programs",
    icon: <SchoolIcon />,
    path: "/dashboard/training-programs",
    roles: ["app_admin", "super_admin", "net_center_admin", "franchisee_admin"],
    keywords: [
      "edit training programs",
      "training programs admin",
      "manage training programs",
      "update programs",
      "course content",
    ],
  },
  {
    text: "LMS Builder",
    icon: <LibraryIcon />,
    path: "/dashboard/students/courses",
    roles: ["app_admin", "super_admin", "net_center_admin", "franchisee_admin", "app_operator"],
    permission: "lms_builder",
    keywords: ["course builder", "course management", "build course", "lms courses"],
  },
  {
    text: "Course Management",
    icon: <SchoolIcon />,
    path: "/dashboard/students/courses",
    roles: ["app_admin", "super_admin", "net_center_admin", "franchisee_admin", "app_operator"],
    permission: "lms_builder",
    keywords: ["course builder", "course management", "build course", "lms builder"],
  },
  {
    text: "Course Referrals",
    icon: <SchoolIcon />,
    path: "/dashboard/students/course-referrals",
    roles: ["app_admin", "super_admin", "net_center_admin", "franchisee_admin"],
    keywords: [
      "course referrals",
      "peer referral",
      "learn zone referral",
      "whatsapp course link",
      "commission settle",
      "promoter",
    ],
  },
  {
    text: "Learn Zone Approvals",
    icon: <SchoolIcon />,
    path: "/content/learn-zone-approvals",
    roles: ["app_admin", "super_admin", "app_operator"],
    keywords: [
      "learn zone",
      "local tutors",
      "online courses",
      "campus classes",
      "mitra courses",
      "course approval",
    ],
  },
  {
    text: "Students Management",
    icon: <PersonAddIcon />,
    path: "/dashboard/students",
    roles: ["app_admin", "super_admin", "net_center_admin", "franchisee_admin"],
    keywords: ["student management", "student list", "manage students"],
  },
  {
    text: "Student Reports",
    icon: <AssessmentIcon />,
    path: "/dashboard/students/reports",
    roles: ["app_admin", "super_admin", "net_center_admin", "franchisee_admin"],
    keywords: [
      "students report",
      "student reports",
      "students reports",
      "student performance",
      "performance report",
      "lms reports",
      "progress report",
      "left days",
      "course progress",
    ],
  },
  {
    text: "Create Student",
    icon: <PersonAddIcon />,
    path: "/dashboard/students/register",
    roles: ["app_admin", "super_admin", "net_center_admin", "franchisee_admin"],
    keywords: ["add student", "register student", "new student"],
  },
  {
    text: "App Notifications",
    icon: <NotificationsIcon />,
    path: "/appnotifications",
    roles: ["app_admin", "super_admin"],
    keywords: [
      "app notifications",
      "push notifications",
      "alerts",
      "knls students",
      "lms notifications",
      "mitra notifications",
      "citizen notifications",
      "send alert",
      "mobile app alert",
      "inbox",
    ],
  },
  {
    text: "Track Sheets Dashboard",
    icon: <AssessmentIcon />,
    path: "/dashboard/students/track-sheets",
    roles: ["app_admin", "super_admin", "net_center_admin", "franchisee_admin"],
    keywords: ["track sheets", "student tracking", "attendance", "daily sheets"],
  },
  {
    text: "Coupons Management",
    icon: <CouponIcon />,
    path: "/coupons",
    roles: [
      "franchisee_admin",
      "net_center_admin",
      "franchisee_operator",
      "app_admin",
      "super_admin",
      "business_admin",
      "shop_admin",
    ],
    keywords: [
      "coupons",
      "redeem coupons",
      "coupon management",
      "discount",
      "offers",
    ],
  },
  {
    text: "Shop Orders",
    icon: <ReceiptLongIcon />,
    path: "/shop-orders",
    roles: [
      "franchisee_admin",
      "net_center_admin",
      "franchisee_operator",
      "app_admin",
      "super_admin",
      "app_operator",
      "business_admin",
      "shop_admin",
    ],
    keywords: [
      "shop orders",
      "citizen orders",
      "indent",
      "redeem",
      "redeem coupons",
      "mitra orders",
    ],
  },
  {
    text: "Job Alerts Management",
    icon: <WorkIcon />,
    path: "/job-alerts",
    roles: ["app_admin", "super_admin", "franchisee_admin", "net_center_admin", "business_admin", "shop_admin"],
  },
  {
    text: "J Alerts Mgmt",
    icon: <WorkIcon />,
    path: "/jalertsmngmt",
    roles: ["app_admin", "super_admin"],
  },
  {
    text: "Smart Job Alert Create",
    icon: <AutoAwesomeIcon />,
    path: "/job-alerts/smart-create",
    roles: ["app_admin", "super_admin", "app_operator"],
    permission: "job_alerts_smart_create",
  },
  {
    text: "Job Alert Subscribers",
    icon: <PersonAddIcon />,
    path: "/job-alerts/subscribers/manage",
    roles: ["app_admin", "super_admin"],
  },

  // App Admin - Full Access
  {
    text: "Dashboard",
    icon: <DashboardIcon />,
    path: "/dashboard",
    roles: ["app_admin", "super_admin", "app_operator"],
  },
  {
    text: "Admin Control Screens",
    icon: <TuneIcon />,
    path: "/admin-control-screens",
    roles: ["app_admin", "super_admin", "app_operator"],
    keywords: [
      "admin control screens",
      "admin controls",
      "screens controls",
      "mitra screens",
      "citizen screens",
      "wheel control",
      "lucky wheel",
      "boost control",
      "approvals",
      "dashboard cards",
      "career resume",
      "town pulse",
      "coupon flow",
      "unified controls",
    ],
  },
  {
    text: "Citizen Users CRUD",
    icon: <PeopleIcon />,
    path: "/users",
    roles: ["app_admin", "super_admin"],
    keywords: [
      "citizen users crud",
      "citizen users",
      "user management",
      "end user",
      "users list",
    ],
  },
  {
    text: "User Analytics",
    icon: <AssessmentIcon />,
    path: "/users/analytics",
    roles: ["app_admin", "super_admin"],
  },
  {
    text: "vyapar_failedotp",
    icon: <SmsFailedIcon />,
    path: "/vyapar_failedotp",
    roles: ["app_admin", "super_admin", "app_operator"],
    keywords: [
      "vyapar_failedotp",
      "failed otp",
      "mobile otp failed",
      "vyapar otp",
      "otp follow up",
    ],
  },
  {
    text: "vyapar registration form",
    icon: <VerifiedUserIcon />,
    path: "/business-admin/register",
    roles: ["app_admin", "super_admin", "app_operator"],
    permission: "vyapar_assisted_registration",
    keywords: [
      "vyapar registration form",
      "mitra register form",
      "mitra registration by admin",
      "mitra registration",
      "register without otp",
      "admin assisted mitra",
      "vyapar registration",
      "assisted registration",
      "register vyapar",
      "business-admin/register",
    ],
  },
  {
    text: "Mitra Users CRUD",
    icon: <VerifiedUserIcon />,
    path: "/business-admin/verification",
    roles: ["app_admin", "super_admin", "app_operator"],
    permission: "business_admin_approval",
    keywords: [
      "mitra users crud",
      "mitra users",
      "business admin verification",
      "franchisee admin",
      "mitra list",
      "manage loksy mitras",
      "manage locsy mitras",
      "manage mitras",
      "loksy mitras",
      "locsy mitras",
    ],
  },
  {
    text: "Shops & Services Index",
    icon: <StoreIcon />,
    path: "/business-category-index",
    roles: ["app_admin", "super_admin"],
    keywords: ["shops services index", "business categories", "flex printers", "kirana", "registration categories"],
  },
  {
    text: "Mitra Token Board Settings",
    icon: <TuneIcon />,
    path: "/mitra-token-board-settings",
    roles: ["app_admin", "super_admin", "app_operator"],
    keywords: [
      "token board",
      "prepaid",
      "postpaid",
      "restaurant",
      "mitra token board settings",
    ],
  },
  {
    text: "Citizen Posting Categories",
    icon: <StoreIcon />,
    path: "/citizen-posting-categories",
    roles: ["app_admin", "super_admin", "app_operator"],
    keywords: ["citizen postings", "olx", "requirements", "to let", "user listings"],
  },
  {
    text: "Citizen Listings",
    icon: <VerifiedUserIcon />,
    path: "/user-postings",
    roles: ["app_admin", "super_admin", "app_operator"],
    keywords: [
      "approve citizen posts",
      "user postings",
      "citizenslistings",
      "olx",
      "marketplace",
      "pause resume listings",
      "requirements moderation",
    ],
  },
  {
    text: "Manage Ads",
    icon: <AdsIcon />,
    path: "/content/ad-settings",
    roles: ["app_admin", "super_admin"],
    keywords: ["advertisement settings", "promotion prices", "popup home rates"],
  },
  {
    text: "Posts By Businesses",
    icon: <AdsIcon />,
    path: "/content/business-posts",
    roles: ["app_admin", "super_admin", "app_operator"],
    keywords: ["business posts", "live demo posts", "suspend ads", "digital pamphlets"],
  },
  {
    text: "Town Pulse Screen",
    icon: <AdsIcon />,
    path: "/content/townpulsescreen",
    roles: ["app_admin", "super_admin", "app_operator"],
    keywords: [
      "town pulse",
      "townpulsescreen",
      "town pulse screen",
      "my town updates",
      "town status reels",
      "demo ads",
      "live ads",
      "pause delete views",
    ],
  },
  {
    text: "Coupon Flow",
    icon: <AdsIcon />,
    path: "/content/coupon-flow",
    roles: ["app_admin", "super_admin"],
    keywords: [
      "coupon flow",
      "digidrop",
      "share with locsy",
      "ad rewards",
      "popup coupons",
      "pamphlet coupons",
      "ad coupon rules",
    ],
  },
  {
    text: "Boost Control",
    icon: <WalletIcon />,
    path: "/content/boost-control",
    roles: ["app_admin", "super_admin"],
    keywords: [
      "boost control",
      "locsy boost",
      "coins",
      "points",
      "referral credits",
      "coinscontrol",
      "referral rewards",
      "/content/boost-control",
    ],
  },
  {
    text: "Coin Management",
    icon: <WalletIcon />,
    path: "/content/coin-management",
    roles: [],
    keywords: [
      "coin management",
      "coins",
      "wallet",
      "wallet management",
      "wallet balance",
      "add points",
      "add coins",
      "recharge plans",
      "earn rules",
      "spend rules",
      "coin ledger",
      "coin analytics",
      "coin terms",
      "refund policy",
      "/content/coin-management",
      "/wallet",
    ],
  },
  {
    text: "Razorpay",
    icon: <WalletIcon />,
    path: "/content/razorpay",
    roles: ["app_admin", "super_admin", "app_operator"],
    keywords: [
      "razorpay",
      "razor pay",
      "razor-pay",
      "payments",
      "receipts",
      "settlement",
      "settled",
      "bank",
      "utr",
      "inflow",
      "outflow",
      "refunds",
      "/content/razorpay",
    ],
  },
  {
    text: "Membership Plans",
    icon: <VerifiedUserIcon />,
    path: "/content/membership-plans",
    roles: ["app_admin", "super_admin"],
    keywords: [
      "membership plans",
      "membership",
      "loksy",
      "basic",
      "advance",
      "prime",
      "free listing",
      "saas membership",
      "plan pricing",
      "/content/membership-plans",
    ],
  },
  {
    text: "Subscription counts",
    icon: <VerifiedUserIcon />,
    path: "/content/membership-subscribers",
    roles: ["app_admin", "super_admin"],
    keywords: [
      "subscribers",
      "subscription counts",
      "trial",
      "basic",
      "advance",
      "prime",
      "/content/membership-subscribers",
    ],
  },
  {
    text: "Locsy Spin",
    icon: <AutoAwesomeIcon />,
    path: "/content/lucky-wheel",
    roles: ["app_admin", "super_admin"],
    keywords: [
      "locsy spin",
      "lucky wheel",
      "spin wheel",
      "shop spin",
      "listing spin",
      "bill bonus spin",
      "probability coupons",
      "/content/lucky-wheel",
    ],
  },
  {
    text: "My Operators",
    icon: <PersonAddIcon />,
    path: "/app-admin/my-operators",
    roles: ["app_admin"],
  },
  {
    text: "Wallet Management",
    icon: <WalletIcon />,
    path: "/content/coin-management",
    roles: ["app_admin", "super_admin"],
    keywords: [
      "wallet management",
      "wallet",
      "add points",
      "add coins",
      "loksy boost",
      "credits",
      "points",
      "/wallet",
    ],
  },
  {
    text: "Shops Management",
    icon: <StoreIcon />,
    path: "/shops",
    roles: ["app_admin", "super_admin"],
  },
  {
    text: "Settings",
    icon: <SettingsIcon />,
    path: "/settings",
    roles: ["app_admin", "super_admin"],
  },
  {
    text: "Dashboard Cards Control",
    icon: <DashboardCustomizeIcon />,
    path: "/dashboard-cards",
    roles: ["app_admin", "super_admin"],
  },
  {
    text: "Resume Controls",
    icon: <DescriptionIcon />,
    path: "/content/career-resume-settings",
    roles: ["app_admin", "super_admin"],
    keywords: [
      "career profile phone",
      "resume controls",
      "resume price",
      "pdf fee",
      "docx fee",
      "edit fee",
      "phone editable",
    ],
  },
  {
    text: "Catalog templates",
    icon: <StoreIcon />,
    path: "/catalog-templates",
    roles: ["app_admin", "super_admin"],
    keywords: [
      "catalogue",
      "catalog",
      "catalogue templates",
      "catalog templates",
      "smart bill defaults",
      "kirana",
      "product templates",
      "hsn",
      "gst",
    ],
  },
  {
    text: "Mitra Screens",
    icon: <PhoneAndroidIcon />,
    path: "/mitrascreens",
    roles: ["app_admin", "super_admin"],
    keywords: [
      "mitra",
      "mitrascreens",
      "mitra screens",
      "/mitrascreens",
      "locsy mitra",
      "citizen interaction",
      "internal operations",
      "partner services",
      "dashboard cards",
      "tagline",
      "thumbnail",
      "badge",
      "quick action",
      "home",
      "grow",
      "operate",
    ],
  },
  {
    text: "Loksy Home Screen Controls",
    icon: <PhoneAndroidIcon />,
    path: "/loksyhomescreen_controls",
    roles: ["app_admin", "super_admin"],
    keywords: [
      "loksyhomescreen_controls",
      "loksy home screen controls",
      "citizen",
      "citizen screens",
      "citizenscreens",
      "/citizenscreens",
      "/loksyhomescreen_controls",
      "locsy citizen",
      "home carousel",
      "home categories",
      "govt alerts",
      "real estate",
      "movies events",
      "fresh market",
      "food zone",
      "carousel order",
      "hide carousel",
      "display section",
      "spotlight",
      "thumbnail",
      "home merchandising",
      "section order",
    ],
  },
  {
    text: "MyTown Screens",
    icon: <GridViewIcon />,
    path: "/mytownscreens",
    roles: ["app_admin", "super_admin"],
    keywords: [
      "mytown",
      "my town",
      "mytownscreens",
      "/mytownscreens",
      "category cards",
      "kirana",
      "provision",
      "fancy shops",
      "category order",
      "category thumbnails",
      "mytown merchandising",
    ],
  },
  {
    text: "Testers Feedback",
    icon: <FeedbackOutlinedIcon />,
    path: "/testersfeedback",
    roles: ["app_admin", "super_admin"],
    keywords: [
      "testers feedback",
      "testersfeedback",
      "/testersfeedback",
      "tester feedback",
      "beta feedback",
      "bug report",
      "ui feedback",
      "mitra feedback",
      "citizen feedback",
      "suggestions",
      "qa",
      "testing",
    ],
  },
  {
    text: "My Tables",
    icon: <TableChartIcon />,
    path: "/tables",
    roles: ["app_admin", "super_admin"],
  },
  {
    text: "Old Site Database",
    icon: <HistoryIcon />,
    path: "/oldsite",
    roles: ["app_admin", "super_admin"],
  },
  {
    text: "LD2HD Upscaler",
    icon: <HighQualityIcon />,
    path: "/ld2hd",
    roles: [
      "app_admin",
      "super_admin",
      "app_operator",
      "franchisee_admin",
      "net_center_admin",
    ],
    keywords: ["ld2hd", "upscale", "image enhance", "photo hd", "super resolution"],
  },

  // Franchisee Admin - high level
  {
    text: "Business Dashboard",
    icon: <StoreIcon />,
    path: "/locsy/dashboard",
    roles: [
      "franchisee_admin",
      "net_center_admin",
      "franchisee_operator",
      "business_admin",
      "shop_admin",
    ],
    keywords: ["business dashboard", "locsy dashboard", "business admin dashboard"],
  },
  {
    text: "Products",
    icon: <StoreIcon />,
    path: "/locsy/products",
    roles: ["franchisee_admin", "net_center_admin", "business_admin", "shop_admin"],
    keywords: ["products", "product service", "products services", "product group", "service group", "inventory", "stock", "low stock", "out of stock", "set taxes", "taxes", "catalog import", "import products", "export catalog", "vyapar products"],
  },
  {
    text: "Accounts",
    icon: <WalletIcon />,
    path: "/locsy/accounts",
    roles: ["franchisee_admin", "net_center_admin", "business_admin", "shop_admin"],
    keywords: ["accounts", "purchase", "stock purchase", "expenses", "dues", "customer dues"],
  },  {
    text: "My Shop Photos",
    icon: <ImageIcon />,
    path: "/locsy/shop-photos",
    roles: ["franchisee_admin", "net_center_admin", "business_admin", "shop_admin"],
    keywords: ["my shop photos", "my shop photos upload", "shop photos upload", "photos update", "profile photo", "shop logo", "business photos"],
  },
  {
    text: "Operator Management",
    icon: <PersonAddIcon />,
    path: "/business-admin/my-operators",
    roles: ["franchisee_admin", "net_center_admin"],
    keywords: ["operators", "business operators", "staff management"],
  },
  {
    text: "Publish Ads",
    icon: <AdsIcon />,
    path: "/locsy/ads",
    roles: ["franchisee_admin", "net_center_admin", "business_admin", "shop_admin"],
    keywords: ["ads", "business ads", "publish advertisements"],
  },
  {
    text: "Online Works",
    icon: <ComputerIcon />,
    path: "/online-works",
    roles: ["net_center_admin", "franchisee_admin"],
  },

  // User - Job Alerts, Coupons, Ads View
  {
    text: "User Dashboard",
    icon: <PersonIcon />,
    path: "/user/dashboard",
    roles: ["user", "customer"],
  },
  {
    text: "Job Alerts",
    icon: <WorkIcon />,
    path: "/user/job-alerts",
    roles: ["user", "customer"],
  },
  {
    text: "Coupons",
    icon: <CouponIcon />,
    path: "/user/coupons",
    roles: ["user", "customer"],
    keywords: ["mission control", "home dashboard", "admin dashboard"],
  },
  {
    text: "Advertisements",
    icon: <AdsIcon />,
    path: "/user/ads",
    roles: ["user", "customer"],
    keywords: ["users", "customer management", "user list"],
  },
  // { text: 'New PDF', icon: <CloudDownloadIcon />, path: '/user/pdfresume', roles: ['user', 'customer'] }, // Hidden - page removed
];

const DashboardLayout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasRole, setUser } = useAuth();

  // Only force-logout on other-device takeover. A 401/429/5xx from a badge
  // count or /me poll must not kick a freshly logged-in Google session.
  const handleApiError = useCallback((error, context = "") => {
    const status = error?.response?.status;
    const code = String(error?.response?.data?.code || "").trim();
    if (status === 401 && code === "SESSION_SUPERSEDED") {
      console.warn(`Session superseded in ${context} - logging out`);
      const redirectPath = logout();
      navigate(redirectPath || "/");
      return;
    }

    console.warn(`API error in ${context}:`, error?.message);
  }, [logout, navigate]);
  const [pendingWorksCount, setPendingWorksCount] = useState(0);
  const [enquiriesCount, setEnquiriesCount] = useState(0);
  const [shopOrdersCount, setShopOrdersCount] = useState(0);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [sidebarManuallyToggled, setSidebarManuallyToggled] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState(
    user?.approval_status || null,
  );
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [showApprovalCongrats, setShowApprovalCongrats] = useState(false);
  const [approvalMessage, setApprovalMessage] = useState("");
  const [registrationFeeDue, setRegistrationFeeDue] = useState(false);
  const [registrationTotalPaise, setRegistrationTotalPaise] = useState(0);
  const [canPayRegistrationFee, setCanPayRegistrationFee] = useState(false);
  const [regPaymentBusy, setRegPaymentBusy] = useState(false);
  const [regPaymentError, setRegPaymentError] = useState("");
  const [quickSearchOpen, setQuickSearchOpen] = useState(false);
  const [quickSearchQuery, setQuickSearchQuery] = useState("");

  const API_BASE_URL =
    process.env.REACT_APP_API_URL || "http://localhost:3000/api";

  const lastCountsFetchRef = useRef(0);
  const isFetchingCountsRef = useRef(false);
  const approvalStatusFetchedRef = useRef(false);
  const COUNTS_STALE_MS = 2 * 60 * 1000;

  const isBusinessAdmin =
    user?.role === "franchisee_admin" || user?.role === "net_center_admin";
  const approvalFlagKey = user?.id
    ? `franchisee_approval_congrats_shown_${user.id}`
    : null;

  useEffect(() => {
    setApprovalStatus(user?.approval_status || null);
  }, [user?.approval_status, user?.id]);

  useEffect(() => {
    if (!isBusinessAdmin || !user?.id) {
      approvalStatusFetchedRef.current = false;
      return undefined;
    }

    // Only fetch once per business admin session
    if (approvalStatusFetchedRef.current) {
      return;
    }
    approvalStatusFetchedRef.current = true;

    const fetchApprovalStatus = async (showSpinner = false) => {
      try {
        if (showSpinner) {
          setApprovalLoading(true);
        }

        const token =
          sessionStorage.getItem("admin_token") ||
          sessionStorage.getItem("token") ||
          localStorage.getItem("admin_token") ||
          localStorage.getItem("token");
        if (!token) {
          return;
        }

        const response = await axios.get(
          `${API_BASE_URL}/franchisee/admin/me`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (!response.data?.success || !response.data?.data) {
          return;
        }

        const latestStatus = response.data.data.approval_status || "pending";
        const previousStatus = approvalStatus;
        const me = response.data.data;

        setApprovalStatus(latestStatus);
        setRegistrationFeeDue(Boolean(me.registration_fee_due));
        setRegistrationTotalPaise(Number(me.registration_total_paise) || 0);
        setCanPayRegistrationFee(Boolean(me.can_pay_registration_fee));
        if (setUser) {
          setUser((currentUser) => ({
            ...(currentUser || {}),
            ...response.data.data,
            approval_status: latestStatus,
          }));
        }

        if (latestStatus === "approved") {
          const alreadyShown = approvalFlagKey
            ? localStorage.getItem(approvalFlagKey) === "true"
            : false;
          if (!alreadyShown && previousStatus !== "approved") {
            if (approvalFlagKey) {
              localStorage.setItem(approvalFlagKey, "true");
            }
            setApprovalMessage(
              "Welcome to Locsy Mitra. Your dashboard is ready.",
            );
            setShowApprovalCongrats(true);
            window.setTimeout(() => {
              setShowApprovalCongrats(false);
              window.location.reload();
            }, 2800);
          }
        }
      } catch (error) {
        handleApiError(error, "fetchApprovalStatus");
      } finally {
        setApprovalLoading(false);
      }
    };

    // Only fetch once on mount, no polling
    fetchApprovalStatus(true);

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchApprovalStatus(false);
      }
    };

    // Removed focus/visibility listeners to prevent API call on tab focus or navigation

    return () => {
      // No listeners to remove
    };
  }, [
    API_BASE_URL,
    approvalFlagKey,
    isBusinessAdmin,
    user?.id,
    handleApiError,
  ]);

  const isApprovalLocked = isBusinessAdmin && approvalStatus !== "approved";

  const refreshRegistrationPaymentFromMe = useCallback(async () => {
    const token =
      sessionStorage.getItem("admin_token") ||
      sessionStorage.getItem("token") ||
      localStorage.getItem("admin_token") ||
      localStorage.getItem("token");
    if (!token) return;
    const response = await axios.get(`${API_BASE_URL}/franchisee/admin/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.data?.success || !response.data?.data) return;
    const me = response.data.data;
    setRegistrationFeeDue(Boolean(me.registration_fee_due));
    setRegistrationTotalPaise(Number(me.registration_total_paise) || 0);
    setCanPayRegistrationFee(Boolean(me.can_pay_registration_fee));
    if (setUser) {
      setUser((currentUser) => ({
        ...(currentUser || {}),
        ...me,
        approval_status: me.approval_status,
      }));
    }
  }, [API_BASE_URL, setUser]);

  const handlePayRegistrationFee = useCallback(async () => {
    if (!canPayRegistrationFee || regPaymentBusy) return;
    setRegPaymentError("");
    setRegPaymentBusy(true);
    try {
      const orderRes = await apiClient.post(
        "/franchisee/admin/registration/create-payment-order",
      );
      const payload = orderRes.data?.data;
      if (
        orderRes.data?.success &&
        (payload?.already_paid ||
          payload?.payment_status === "paid" ||
          payload?.waived)
      ) {
        await refreshRegistrationPaymentFromMe();
        return;
      }
      if (!orderRes.data?.success) {
        setRegPaymentError(
          orderRes.data?.message || "Could not start payment. Try again later.",
        );
        if (payload?.waived || payload?.payment_status === "paid") {
          await refreshRegistrationPaymentFromMe();
        }
        return;
      }
      if (!payload?.order) {
        setRegPaymentError(
          orderRes.data?.message || "Could not start payment. Try again later.",
        );
        return;
      }
      const { order, keyId } = payload;
      const rzResponse = await openRazorpayCheckout({
        order,
        keyId,
        customer: {
          name: user?.business_name || user?.contact_person_name || "",
          phone: user?.phone || "",
        },
        description: "Franchisee registration fee",
      });
      await apiClient.post("/franchisee/admin/registration/complete-razorpay", {
        razorpay_order_id: rzResponse.razorpay_order_id,
        razorpay_payment_id: rzResponse.razorpay_payment_id,
        razorpay_signature: rzResponse.razorpay_signature,
      });
      await refreshRegistrationPaymentFromMe();
    } catch (e) {
      if (e?.message === "Payment cancelled") {
        setRegPaymentError("");
        return;
      }
      setRegPaymentError(e.response?.data?.message || e.message || "Payment failed");
    } finally {
      setRegPaymentBusy(false);
    }
  }, [
    canPayRegistrationFee,
    regPaymentBusy,
    user,
    refreshRegistrationPaymentFromMe,
  ]);

  useEffect(() => {
    const shouldFetchCounts = () => {
      const now = Date.now();
      return now - lastCountsFetchRef.current > COUNTS_STALE_MS;
    };

    const fetchCounts = async (force = false) => {
      if (isFetchingCountsRef.current) return;
      if (!force && !shouldFetchCounts()) return;

      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        isFetchingCountsRef.current = true;
        lastCountsFetchRef.current = Date.now();

        // Fetch pending works count (bills with work_status = 'pending' or 'under_processing')
        try {
          const pendingWorksResponse = await axios.get(
            `${API_BASE_URL}/smartbills/bills`,
            {
              params: { limit: 10000 },
            },
          );
          if (
            pendingWorksResponse.data?.success &&
            pendingWorksResponse.data.data
          ) {
            const bills = pendingWorksResponse.data.data || [];
            const pendingCount = bills.filter((bill) => {
              const status = bill.work_status || bill.workStatus || "completed";
              return status === "pending" || status === "under_processing";
            }).length;
            setPendingWorksCount(pendingCount);
          }
        } catch (err) {
          handleApiError(err, "fetchPendingWorksCount");
        }

        // Fetch enquiries count (all enquiries)
        try {
          const enquiriesResponse = await axios.get(
            `${API_BASE_URL}/enquiries`,
            {
              params: { limit: 10000 },
            },
          );
          if (enquiriesResponse.data?.success && enquiriesResponse.data.data) {
            const enquiries = enquiriesResponse.data.data || [];
            setEnquiriesCount(enquiries.length);
          }
        } catch (err) {
          handleApiError(err, "fetchEnquiriesCount");
        }

        // Shop orders (citizen redeem) pending badge — top AppBar
        try {
          const role = user?.role;
          if (role && SHOP_ORDERS_ROLES.includes(role)) {
            const count = await shopIndentsApi.pendingCount();
            setShopOrdersCount(Number(count) || 0);
          } else {
            setShopOrdersCount(0);
          }
        } catch (err) {
          console.warn("Failed to fetch shop orders count:", err?.message || err);
        }
      } catch (err) {
        console.warn("Failed to fetch counts:", err);
      } finally {
        isFetchingCountsRef.current = false;
      }
    };

    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === "visible") {
        fetchCounts();
      }
    };

    fetchCounts(true);
    window.addEventListener("focus", handleVisibilityOrFocus);
    document.addEventListener("visibilitychange", handleVisibilityOrFocus);

    return () => {
      window.removeEventListener("focus", handleVisibilityOrFocus);
      document.removeEventListener("visibilitychange", handleVisibilityOrFocus);
    };
  }, [API_BASE_URL, location.pathname, handleApiError, user?.role]);

  // Handle sidebar hover - show on hover when hidden
  const handleSidebarMouseEnter = () => {
    setSidebarHovered(true);
    // Only show via hover if not manually toggled
    if (!sidebarManuallyToggled) {
      setSidebarVisible(true);
    }
  };

  // Hide sidebar when mouse leaves (only if it was shown by hover, not manually toggled)
  const handleSidebarMouseLeave = () => {
    setSidebarHovered(false);
    // Auto-hide only if sidebar was shown via hover (not manually toggled)
    if (!sidebarManuallyToggled) {
      setSidebarVisible(false);
    }
  };

  // Toggle sidebar manually with button
  const handleToggleSidebar = () => {
    const newVisibility = !sidebarVisible;
    setSidebarVisible(newVisibility);
    // Track manual toggle state
    setSidebarManuallyToggled(newVisibility);
    // Reset hover state when manually toggled
    if (newVisibility) {
      setSidebarHovered(false);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    const redirectPath = logout();
    navigate(redirectPath);
  };

  const handleNavigate = (path, event) => {
    // If Ctrl/Cmd is pressed, open in new tab
    if (event && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      const fullUrl = window.location.origin + path;
      window.open(fullUrl, "_blank", "noopener,noreferrer");
      return;
    }

    // Normal navigation
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleQuickSearchOpen = () => {
    setQuickSearchOpen(true);
  };

  const handleQuickSearchClose = () => {
    setQuickSearchOpen(false);
    setQuickSearchQuery("");
  };

  const handleQuickSearchSelect = (path) => {
    setQuickSearchOpen(false);
    setQuickSearchQuery("");
    navigate(path);
  };

  useEffect(() => {
    const handleKeyboardShortcut = (event) => {
      const targetTag = event.target?.tagName?.toLowerCase();
      const isTypingTarget =
        targetTag === "input" ||
        targetTag === "textarea" ||
        event.target?.isContentEditable;

      if (
        (event.ctrlKey || event.metaKey) &&
        event.shiftKey &&
        event.key.toLowerCase() === "f"
      ) {
        event.preventDefault();
        setQuickSearchOpen(true);
      }
    };

    window.addEventListener("keydown", handleKeyboardShortcut);

    return () => {
      window.removeEventListener("keydown", handleKeyboardShortcut);
    };
  }, []);

  const getFilteredMenuItems = () => {
    if (!user) return [];

    const withDescriptions = (items, descriptions) =>
      items.map((item) => ({
        ...item,
        description: descriptions[item.path] || item.description || "",
      }));

    const descriptions = {
      "/dashboard": "Mission control",
      "/admin-control-screens":
        "Screens, approvals, wheel, ads & platform controls in one place",
      "/locsy/dashboard": "Locsy hub",
      "/franchisee/dashboard": "Locsy hub",
      "/dashboard/students": "View, search, and manage students",
      "/dashboard/students/reports": "Student performance, left days, course-wise insights",
      "/dashboard/students/register": "Register a new student directly",
      "/dashboard/students/courses": "Manage LMS courses",
      "/dashboard/students/course-referrals": "Course peer referrals — WhatsApp links, sales, UPI settle status",
      "/dashboard/students/track-sheets": "Track daily sheets and activity",
      "/appnotifications": "Send push and in-app alerts to Students LMS, Mitra, and Citizen apps",
      "/user/dashboard": "Job alerts, coupons & ads",
      "/users": "Citizen users CRUD — list and manage portal users",
      "/users/analytics": "Login statistics & user analytics",
      "/business-admin/verification": "Mitra users CRUD — verify and manage Mitra accounts",
      "/bills/create": "Smart bill flow (QR)",
      "/bills/drafts": "Resume unfinished Smart Bill drafts",
      "/bills/cart-model": "Cart model bill",
      "/bills/view": "History & analytics",
      "/bills/enquiry-form": "Register enquiry — call / WhatsApp when available",
      "/bills/operator-calls": "Call-focused customer list",
      "/bills/pending-works": "Manage pending works & status updates",
      "/bills/enquiries": "Track enquiries & convert to bills",
      "/customer/search": "Search customer & view dashboard",
      "/bills/service-charges": "Counter total & profit statistics",
      "/bills/counter-sales-datewise": "Date-wise scrollable counter tracker",
      "/bills/inventory": "Inventory hub — categories, products, live stock",
      "/bills/main-categories": "Product / service group management (shared with Mitra)",
      "/bills/services": "Products & services catalog (shared with Mitra)",
      "/services": "KNLS government & civic services",
      "/coupons": "Create & manage coupon campaigns",
      "/shop-orders": "Citizen redeem coupon orders inbox",
      "/job-alerts": "Career broadcast",
      "/job-alerts/smart-create": "AI-powered job alert creation",
      "/job-alerts/subscribers/manage": "Manage job alert subscribers",
      "/shops": "Partner network",
      "/net-centers": "Franchise control",
      "/online-works": "Govt & civic",
      "/settings": "Configuration",
      "/tables": "Database tables explorer",
      "/ld2hd": "Free image upscale up to 16x (LD2HD)",
      "/business-admin/approval": "Business admin approvals",
      "/business-category-index": "Shops & services index for registration and My Town",
      "/mitra-token-board-settings":
        "Token Board defaults by category — prepaid, postpaid, packs, tables",
      "/citizen-posting-categories": "Citizen posting types — OLX, jobs, services toggles",
      "/user-postings":
        "Citizen Listings — approve, pause/resume, block unlawful marketplace posts",
      "/franchisee/citizen-requirements": "Citizen requirements inbox for your business",
      "/app-admin/my-operators": "Manage app admin operators",
      "/dashboard/cards": "Dashboard cards control",
      "/catalog-templates": "Smart Bill catalogue / catalog product templates",
      "/mitrascreens": "Mitra Citizen Interaction & Internal Operations cards",
      "/citizenscreens": "Locsy Citizen home carousels — order, hide, covers, spotlight",
      "/loksyhomescreen_controls":
        "Hide / display Citizen home categories (Govt Alerts, Real Estate, Food Zone…)",
      "/mytownscreens": "Locsy Citizen MyTown category cards — images, labels, order & hide",
      "/content/learn-zone-approvals":
        "Approve Local Tutor Online & Campus courses for Citizen Learn Zone",
      "/content/coupon-flow": "DigiDrop — coupons per popup / pamphlet / updates",
      "/content/coupon-management":
        "Citizen Coupon Management — distribution pool, ad types, worth bands, spinner",
      "/content/boost-control":
        "Locsy Boost — referral credits %, validity, checkout cap (coins/points)",
      "/content/coin-management":
        "Loksy Wallet — wallets, recharge, earn/spend rules, ledger, legal disclaimers",
      "/content/razorpay":
        "Razorpay receipts, refunds, bank settlements (inflow / outflow)",
      "/content/membership-plans":
        "Free / Basic / Advance / Prime — prices, trial, volume caps, add-ons",
      "/content/membership-subscribers":
        "Aggregate subscription counts (no phone / Aadhaar)",
      "/content/shop-polls":
        "Mitra customer polls — votes, extra options & shop stats",
      "/apptrends":
        "Day-wise and month-wise Loksy & Vyapar installations, registered users, and activity",
      "/content/lucky-wheel": "Locsy Spin — numbers 1–10 · N coupons + coins",
      "/oldsite": "Legacy dashboard tools",
    };

    const adminControlHubPaths = getAdminControlHubPaths();
    const collapseControlsIntoHub = ADMIN_CONTROL_HUB_ROLES.includes(user.role);
    const isNotCollapsedIntoHub = (item) => {
      if (!collapseControlsIntoHub) return true;
      if (item.path === "/admin-control-screens") return true;
      return !adminControlHubPaths.has(item.path);
    };

    const filterItemsForUser = (sourceItems, { ensureFranchiseeDashboard = false } = {}) => {
      const visibleItems = isLocalInternalHost()
        ? sourceItems
        : sourceItems.filter(
            (item) =>
              item.path !== "/tables" &&
              item.path !== "/oldsite" &&
              item.path !== "/bills/manage",
          );

      if (user.role === "app_operator") {
        const operatorPermissions = expandLegacyOperatorPermissions(
          Array.isArray(user.permissions) ? user.permissions : [],
        );
        return visibleItems.filter((item) => {
          if (!item.roles || !item.roles.includes("app_operator")) return false;
          if (!isNotCollapsedIntoHub(item)) return false;
          if (item.path === "/dashboard") return true;
          if (item.path === "/admin-control-screens") {
            return operatorHasAnyAdminControlPermission(operatorPermissions);
          }

          if (item.permission) {
            const requiredPermissions = Array.isArray(item.permission)
              ? item.permission
              : [item.permission];
            return requiredPermissions.some((permission) =>
              operatorPermissions.includes(permission),
            );
          }

          return true;
        });
      }

      if (user.role === "franchisee_operator") {
        const operatorPermissions = Array.isArray(user.permissions)
          ? user.permissions
          : [];
        const items = visibleItems.filter((item) =>
          franchiseeOperatorCanAccessPath(operatorPermissions, item.path),
        );
        if (!ensureFranchiseeDashboard) return items;
        const hasDashboard = items.some((item) => item.path === "/locsy/dashboard");
        return hasDashboard
          ? items
          : [
              {
                text: "Business Dashboard",
                icon: <StoreIcon />,
                path: "/locsy/dashboard",
                description: descriptions["/locsy/dashboard"],
              },
              ...items,
            ];
      }

      return visibleItems.filter((item) => {
        if (!item.roles?.some((role) => hasRole(role))) return false;
        if (!isNotCollapsedIntoHub(item)) return false;

        if (user.role === "franchisee_admin" || user.role === "net_center_admin") {
          return pathnameAllowedByFranchiseFeatures(item.path, user.features);
        }

        return true;
      });
    };

    if (user.role === "app_operator") {
      return withDescriptions(filterItemsForUser(menuItems), descriptions);
    }

    if (user.role === "franchisee_operator") {
      return withDescriptions(
        filterItemsForUser(menuItems, { ensureFranchiseeDashboard: true }),
        descriptions,
      );
    }

    return withDescriptions(filterItemsForUser(menuItems), descriptions);
  };

  const filteredMenuItems = getFilteredMenuItems();

  const findablePages = useMemo(() => {
    if (!user) return [];

    const filterExtraForUser = (sourceItems) => {
      if (user.role === "app_operator") {
        const operatorPermissions = expandLegacyOperatorPermissions(
          Array.isArray(user.permissions) ? user.permissions : [],
        );
        return sourceItems.filter((item) => {
          if (!item.roles || !item.roles.includes("app_operator")) return false;
          if (item.permission) {
            const requiredPermissions = Array.isArray(item.permission)
              ? item.permission
              : [item.permission];
            return requiredPermissions.some((permission) =>
              operatorPermissions.includes(permission),
            );
          }
          return true;
        });
      }

      if (user.role === "franchisee_operator") {
        const operatorPermissions = Array.isArray(user.permissions)
          ? user.permissions
          : [];
        return sourceItems.filter((item) =>
          franchiseeOperatorCanAccessPath(operatorPermissions, item.path),
        );
      }

      return sourceItems.filter((item) => {
        if (!item.roles?.some((role) => hasRole(role))) return false;
        if (user.role === "franchisee_admin" || user.role === "net_center_admin") {
          return pathnameAllowedByFranchiseFeatures(item.path, user.features);
        }
        return true;
      });
    };

    const hubFindPages = ADMIN_CONTROL_HUB_ROLES.includes(user.role)
      ? ADMIN_CONTROL_SECTIONS.flatMap((section) => section.items)
          .filter((item) => userCanAccessAdminControlItem(user, item))
          .map((item) => ({
            text: item.title,
            icon: <TuneIcon />,
            path: item.path,
            roles: item.roles,
            permission: item.permission,
            description: item.description,
            keywords: item.keywords || [],
          }))
      : [];

    return mergeFindPageLists(
      filteredMenuItems,
      filterExtraForUser([...EXTRA_FIND_PAGES, ...hubFindPages]),
    );
  }, [user, filteredMenuItems, hasRole]);

  const displayShopName =
    user?.business_name || user?.shop_name || user?.shopName || "";

  const displayProprietorName =
    user?.proprietor_name ||
    user?.proprietorName ||
    user?.owner_name ||
    user?.contact_person_name ||
    user?.full_name ||
    "Admin";

  const avatarSeedName = displayProprietorName || displayShopName || "Admin";

  const quickLinkResults = useMemo(() => {
    const query = quickSearchQuery.trim().toLowerCase();
    const items = findablePages.filter((item) => {
      if (!query) return true;
      return buildPageSearchHaystack(item).includes(query);
    });
    return items;
  }, [findablePages, quickSearchQuery]);

  const quickSearchResults = useMemo(() => {
    const deduped = [];
    const seen = new Set();

    quickLinkResults.forEach((item) => {
      const key = item.id || `${item.path}-${item.text}`;
      if (!seen.has(key)) {
        seen.add(key);
        deduped.push(item);
      }
    });

    return deduped;
  }, [quickLinkResults]);

  const drawer = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background:
          "linear-gradient(180deg, #0f172a 0%, #1e293b 35%, #111827 100%)",
        backgroundColor: "#0f172a", // Fallback solid color
        backgroundAttachment: "fixed",
        color: "#ffffff !important",
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
        // Force hardware acceleration to prevent rendering issues during zoom
        transform: "translateZ(0)",
        willChange: "auto",
        // Support browser search highlighting
        "& *::selection": {
          backgroundColor: "#3b82f6",
          color: "#ffffff",
        },
        "& *::-moz-selection": {
          backgroundColor: "#3b82f6",
          color: "#ffffff",
        },
        // Ensure all child elements maintain colors during zoom
        "& *": {
          color: "#ffffff !important",
          WebkitTextFillColor: "#ffffff !important",
        },
      }}
    >
      <Toolbar
        sx={{
          minHeight: 72,
          px: 3,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Avatar
          sx={{
            bgcolor: "primary.main",
            width: 40,
            height: 40,
            fontWeight: 700,
          }}
        >
          L
        </Avatar>
        <Box>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            sx={{
              color: "#ffffff !important",
              WebkitFontSmoothing: "antialiased",
              MozOsxFontSmoothing: "grayscale",
            }}
          >
            LOCSY Admin
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "rgba(255,255,255,0.8) !important",
              WebkitFontSmoothing: "antialiased",
              MozOsxFontSmoothing: "grayscale",
            }}
          >
            Unified control hub
          </Typography>
        </Box>
      </Toolbar>
      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mb: 1 }} />

      <List sx={{ px: 1.5, flexGrow: 1 }}>
        {filteredMenuItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                component={Link}
                to={item.path}
                selected={isActive}
                onClick={(e) => {
                  // If Ctrl/Cmd is pressed, let browser handle it (open in new tab)
                  // Link component will handle this automatically, but we close mobile drawer
                  if (!(e.ctrlKey || e.metaKey) && isMobile) {
                    setMobileOpen(false);
                  }
                }}
                sx={{
                  borderRadius: 2,
                  px: 2,
                  py: 1.2,
                  transition: "all 0.25s ease",
                  backdropFilter: isActive ? "blur(4px)" : "none",
                  cursor: "pointer",
                  color: "#ffffff !important",
                  textDecoration: "none",
                  WebkitFontSmoothing: "antialiased",
                  MozOsxFontSmoothing: "grayscale",
                  // Ensure text is always visible
                  "&, & *": {
                    color: "#ffffff !important",
                  },
                  // Browser search highlighting support
                  "&::selection, & *::selection": {
                    backgroundColor: "#3b82f6 !important",
                    color: "#ffffff !important",
                  },
                  "&::-moz-selection, & *::-moz-selection": {
                    backgroundColor: "#3b82f6 !important",
                    color: "#ffffff !important",
                  },
                  "&.Mui-selected": {
                    background:
                      "linear-gradient(90deg, rgba(59,130,246,0.25), rgba(59,130,246,0.1))",
                    boxShadow: "inset 0 0 0 1px rgba(59,130,246,0.35)",
                    color: "#ffffff !important",
                    "&:hover": {
                      background:
                        "linear-gradient(90deg, rgba(59,130,246,0.35), rgba(59,130,246,0.15))",
                      color: "#ffffff !important",
                    },
                    "& .MuiListItemIcon-root": {
                      color: "#60A5FA !important",
                    },
                    "& .MuiTypography-root": {
                      color: "#ffffff !important",
                    },
                  },
                  "&:hover": {
                    background: "rgba(255,255,255,0.12)",
                    color: "#ffffff !important",
                  },
                  "& .MuiListItemIcon-root": {
                    color: isActive
                      ? "#60A5FA !important"
                      : "rgba(255,255,255,0.85) !important",
                  },
                  "& .MuiTypography-root": {
                    color: "#ffffff !important",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive
                      ? "#60A5FA !important"
                      : "rgba(255,255,255,0.8) !important",
                    minWidth: 36,
                    WebkitFontSmoothing: "antialiased",
                    MozOsxFontSmoothing: "grayscale",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        color: "#ffffff !important",
                        WebkitFontSmoothing: "antialiased",
                        MozOsxFontSmoothing: "grayscale",
                      }}
                    >
                      {item.text}
                      {item.path === "/bills/pending-works" &&
                        pendingWorksCount > 0 && (
                          <span style={{ marginLeft: "8px", color: "#ffc107" }}>
                            ({pendingWorksCount})
                          </span>
                        )}
                      {item.path === "/bills/operator-calls" &&
                        enquiriesCount > 0 && (
                          <span style={{ marginLeft: "8px", color: "#ffc107" }}>
                            ({enquiriesCount})
                          </span>
                        )}
                    </Typography>
                  }
                  secondary={
                    item.description ? (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(255,255,255,0.7) !important",
                          WebkitFontSmoothing: "antialiased",
                          MozOsxFontSmoothing: "grayscale",
                        }}
                      >
                        {item.description}
                      </Typography>
                    ) : null
                  }
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Box sx={{ p: 2, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)" }}>
          © {new Date().getFullYear()} LOCSY • Admin Console
        </Typography>
      </Box>
    </Box>
  );

  const isFullBleedPage =
    location.pathname === "/bills/view" ||
    location.pathname === "/bills/advance-bills";

  return (
    <>
      {/* Global styles for navigation bar search highlighting and zoom fix */}
      <style>{`
        /* Navigation bar search highlighting */
        .MuiDrawer-paper *::selection {
          background-color: #3b82f6 !important;
          color: #ffffff !important;
        }
        .MuiDrawer-paper *::-moz-selection {
          background-color: #3b82f6 !important;
          color: #ffffff !important;
        }
        /* Fix for zoom in/out - ensure background and text colors persist */
        .MuiDrawer-paper {
          background: linear-gradient(180deg, #0f172a 0%, #1e293b 35%, #111827 100%) !important;
          background-color: #0f172a !important;
          background-attachment: fixed !important;
          -webkit-text-fill-color: #ffffff !important;
          color: #ffffff !important;
          /* Prevent color changes during zoom */
          -webkit-font-smoothing: antialiased !important;
          -moz-osx-font-smoothing: grayscale !important;
          /* Force hardware acceleration to prevent rendering issues */
          transform: translateZ(0);
          will-change: auto;
        }
        /* Ensure all text elements maintain white color during zoom */
        .MuiDrawer-paper *,
        .MuiDrawer-paper *::before,
        .MuiDrawer-paper *::after {
          -webkit-text-fill-color: #ffffff !important;
          color: #ffffff !important;
        }
        /* Override any white backgrounds that might appear during zoom */
        .MuiDrawer-paper .MuiListItemButton-root {
          background-color: transparent !important;
          background: transparent !important;
        }
        .MuiDrawer-paper .MuiListItemButton-root:hover {
          background-color: rgba(255,255,255,0.12) !important;
          background: rgba(255,255,255,0.12) !important;
        }
        .MuiDrawer-paper .MuiListItemButton-root.Mui-selected {
          background: linear-gradient(90deg, rgba(59,130,246,0.25), rgba(59,130,246,0.1)) !important;
          background-color: rgba(59,130,246,0.25) !important;
        }
        /* Ensure icons maintain color */
        .MuiDrawer-paper .MuiListItemIcon-root,
        .MuiDrawer-paper .MuiListItemIcon-root * {
          color: rgba(255,255,255,0.85) !important;
          -webkit-text-fill-color: rgba(255,255,255,0.85) !important;
        }
        /* Ensure typography maintains white color */
        .MuiDrawer-paper .MuiTypography-root,
        .MuiDrawer-paper .MuiTypography-root * {
          color: #ffffff !important;
          -webkit-text-fill-color: #ffffff !important;
        }
        /* Fix for Toolbar background */
        .MuiDrawer-paper .MuiToolbar-root {
          background-color: transparent !important;
          background: transparent !important;
        }
        /* Fix for Avatar background */
        .MuiDrawer-paper .MuiAvatar-root {
          background-color: rgba(59,130,246,0.8) !important;
        }
      `}</style>

      <Backdrop
        open={showApprovalCongrats}
        sx={{
          zIndex: theme.zIndex.modal + 2,
          color: "#fff",
          backdropFilter: "blur(8px)",
        }}
      >
        <Fade in={showApprovalCongrats}>
          <Paper
            elevation={24}
            sx={{
              px: 4,
              py: 4,
              maxWidth: 420,
              textAlign: "center",
              borderRadius: 4,
              background:
                "linear-gradient(135deg, rgba(17,24,39,0.96), rgba(30,64,175,0.96))",
              border: "1px solid rgba(255,255,255,0.12)",
            }}
          >
            <Box sx={{ fontSize: 56, lineHeight: 1, mb: 1 }}>🎉</Box>
            <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>
              Congrats & welcome
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "rgba(255,255,255,0.92)", mt: 1 }}
            >
              Your Mitra account is active. Citizen listing shows Not verified
              until an admin verifies your profile.
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "rgba(255,255,255,0.88)" }}
            >
              {approvalMessage ||
                "Your business dashboard access is now unlocked."}
            </Typography>
          </Paper>
        </Fade>
      </Backdrop>

      {/* Hover Zone - Left edge when sidebar is hidden */}
      {!sidebarVisible && (
        <Box
          onMouseEnter={handleSidebarMouseEnter}
          sx={{
            position: "fixed",
            left: 0,
            top: 0,
            width: "20px",
            height: "100vh",
            zIndex: 1299,
            display: { xs: "none", md: "block" },
            cursor: "pointer",
          }}
        />
      )}

      {/* Toggle Button - Fixed position, always visible at center */}
      <Box
        sx={{
          position: "fixed",
          left: sidebarVisible ? drawerWidth : 0,
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 1300,
          transition: "left 0.3s ease-in-out",
          display: { xs: "none", md: "block" },
        }}
      >
        <IconButton
          onClick={handleToggleSidebar}
          sx={{
            bgcolor: "rgba(102, 126, 234, 0.95)",
            color: "#ffffff",
            backdropFilter: "blur(10px)",
            boxShadow: "0 4px 20px rgba(102, 126, 234, 0.4)",
            border: "2px solid rgba(255, 255, 255, 0.3)",
            borderRadius: "0 12px 12px 0",
            width: 40,
            height: 64,
            "&:hover": {
              bgcolor: "rgba(118, 75, 162, 0.95)",
              boxShadow: "0 6px 25px rgba(102, 126, 234, 0.5)",
              transform: "scale(1.05)",
            },
            transition: "all 0.3s ease",
          }}
        >
          {sidebarVisible ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Box>

      <Box sx={{ display: "flex", width: "100%", maxWidth: "100%" }}>
        <AppBar
          position="fixed"
          sx={{
            width: {
              md: sidebarVisible ? `calc(100% - ${drawerWidth}px)` : "100%",
            },
            ml: { md: sidebarVisible ? `${drawerWidth}px` : 0 },
            background:
              "linear-gradient(90deg, #0F172A 0%, #1E3A8A 45%, #2563EB 100%)",
            boxShadow: "0 10px 30px rgba(15,23,42,0.25)",
            transition: "margin-left 0.3s ease-in-out, width 0.3s ease-in-out",
            zIndex: theme.zIndex.drawer + 1,
          }}
        >
          <Toolbar sx={{ minHeight: { xs: 56, md: 72 }, px: { xs: 1, sm: 2 } }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: { xs: 1, sm: 2 }, display: { md: "none" } }}
            >
              <MenuIcon />
            </IconButton>
            <Tooltip title="Home - Go to Dashboard">
              <Button
                color="inherit"
                startIcon={<HomeIcon />}
                onClick={() => {
                  // Context-aware navigation: stay in the same dashboard type
                  if (location.pathname.startsWith("/user/")) {
                    navigate("/user/dashboard");
                  } else if (
                    location.pathname.startsWith("/locsy/") ||
                    location.pathname.startsWith("/franchisee/")
                  ) {
                    navigate("/locsy/dashboard");
                  } else {
                    navigate("/dashboard");
                  }
                }}
                sx={{
                  mr: { xs: 0.5, sm: 2 },
                  minWidth: { xs: 40, sm: 64 },
                  px: { xs: 1, sm: 2 },
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
                  Home
                </Box>
              </Button>
            </Tooltip>
            {(user?.role === "app_admin" || user?.role === "super_admin") && (
              <Tooltip title="User Analytics - Login Statistics">
                <Button
                  color="inherit"
                  startIcon={<AssessmentIcon />}
                  onClick={() => navigate("/users/analytics")}
                  sx={{
                    mr: { xs: 0.5, sm: 2 },
                    minWidth: { xs: 40, sm: 64 },
                    px: { xs: 1, sm: 2 },
                    display: { xs: "none", lg: "inline-flex" },
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  User Analytics
                </Button>
              </Tooltip>
            )}
            <Tooltip title="Find pages (Ctrl+Shift+F)">
              <Button
                color="inherit"
                startIcon={<SearchIcon />}
                onClick={handleQuickSearchOpen}
                sx={{
                  mr: { xs: 0.5, sm: 2 },
                  minWidth: { xs: 40, sm: 64 },
                  px: { xs: 1, sm: 2 },
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 2,
                  border: "1px solid rgba(255,255,255,0.25)",
                  backgroundColor: "rgba(255,255,255,0.08)",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.16)",
                    borderColor: "rgba(255,255,255,0.4)",
                  },
                }}
              >
                <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                  Find
                </Box>
              </Button>
            </Tooltip>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                flexGrow: 1,
                minWidth: 0,
                fontSize: { xs: "0.95rem", sm: "1.15rem", md: "1.25rem" },
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {filteredMenuItems.find((item) => item.path === location.pathname)
                ?.text || "Dashboard"}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 2 } }}>
              {filteredMenuItems.some((item) => item.path === "/shop-orders") && (
                <Tooltip title="Shop Orders — citizen redeem orders">
                  <IconButton
                    color="inherit"
                    onClick={() => navigate("/shop-orders")}
                    aria-label="Shop orders"
                  >
                    <Badge
                      badgeContent={shopOrdersCount}
                      color="error"
                      max={99}
                      showZero={false}
                      overlap="circular"
                      sx={{
                        "& .MuiBadge-badge": {
                          fontWeight: 700,
                          fontSize: "0.65rem",
                          minWidth: 18,
                          height: 18,
                        },
                      }}
                    >
                      <ReceiptLongIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>
              )}
              <IconButton color="inherit">
                <Badge badgeContent={0} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                }}
                onClick={handleMenuOpen}
              >
                <Avatar
                  sx={{ width: 32, height: 32, bgcolor: "secondary.main" }}
                >
                  {avatarSeedName?.charAt(0) || "A"}
                </Avatar>
                <Box sx={{ display: { xs: "none", sm: "block" }, minWidth: 0 }}>
                  {displayShopName ? (
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        lineHeight: 1.1,
                        whiteSpace: "nowrap",
                        maxWidth: 170,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {displayShopName}
                    </Typography>
                  ) : null}
                  <Typography
                    variant="caption"
                    sx={{
                      opacity: 0.92,
                      lineHeight: 1.1,
                      whiteSpace: "nowrap",
                      maxWidth: 170,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "block",
                    }}
                  >
                    {displayProprietorName}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={() => handleNavigate("/profile")}>
                <ListItemIcon>
                  <AccountCircleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Profile</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Dialog
          open={quickSearchOpen}
          onClose={handleQuickSearchClose}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: 4,
              background:
                "linear-gradient(180deg, rgba(15,23,42,0.98) 0%, rgba(30,41,59,0.98) 100%)",
              color: "#ffffff",
              border: "1px solid rgba(255,255,255,0.12)",
            },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="h6" fontWeight={800}>
              Find pages
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
              Use Ctrl+Shift+F to find any page you can access (including catalogue / catalog templates). Browser Ctrl+F stays available.
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pb: 3 }}>
            <TextField
              autoFocus
              fullWidth
              placeholder="Search available pages in your current dashboard"
              value={quickSearchQuery}
              onChange={(e) => setQuickSearchQuery(e.target.value)}
              onKeyDown={(event) => {
                if (
                  event.key === "Enter" &&
                  quickSearchQuery.trim() &&
                  quickSearchResults.length > 0
                ) {
                  handleQuickSearchSelect(quickSearchResults[0].path);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "rgba(255,255,255,0.72)" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2,
                "& .MuiInputBase-root": {
                  color: "#ffffff",
                  borderRadius: 3,
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(255,255,255,0.16)",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(255,255,255,0.32)",
                },
                "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#60A5FA",
                },
                "& input::placeholder": {
                  color: "rgba(255,255,255,0.5)",
                  opacity: 1,
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.72)", mb: 1, display: "block" }}
            >
              Showing only pages you can access in this dashboard.
            </Typography>
            {quickSearchQuery.trim() && quickSearchResults.length === 0 && (
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.72)", mb: 1 }}
              >
                No matching pages found.
              </Typography>
            )}
            <Stack spacing={1}>
              {quickSearchResults.map((item) => (
                <Paper
                  key={item.id || `${item.path}-${item.text}`}
                  elevation={0}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 3,
                    overflow: "hidden",
                  }}
                >
                  <ListItemButton
                    onClick={() => handleQuickSearchSelect(item.path)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      color: "#ffffff",
                      "&:hover": {
                        backgroundColor: "rgba(96,165,250,0.12)",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: "#93C5FD" }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      secondary={item.description || item.path}
                      primaryTypographyProps={{ fontWeight: 700 }}
                      secondaryTypographyProps={{
                        sx: { color: "rgba(255,255,255,0.7)" },
                      }}
                    />
                  </ListItemButton>
                </Paper>
              ))}
              {quickSearchResults.length === 0 && (
                <Typography sx={{ color: "rgba(255,255,255,0.72)", py: 2 }}>
                  No matching page found.
                </Typography>
              )}
            </Stack>
          </DialogContent>
        </Dialog>

        <Box
          component="nav"
          sx={{
            width: { md: sidebarVisible ? drawerWidth : 0 },
            flexShrink: { md: 0 },
            transition: "width 0.3s ease-in-out",
            overflow: "hidden",
            position: "relative",
          }}
        >
          {/* Mobile Drawer - Only visible on mobile */}
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: "block", md: "none" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: drawerWidth,
                WebkitFontSmoothing: "antialiased",
                MozOsxFontSmoothing: "grayscale",
                imageRendering: "crisp-edges",
                background:
                  "linear-gradient(180deg, #0f172a 0%, #1e293b 35%, #111827 100%) !important",
                backgroundColor: "#0f172a !important",
                transform: "translateZ(0)",
              },
            }}
          >
            {drawer}
          </Drawer>

          {/* Desktop Drawer - Only visible on desktop, single instance */}
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: "none", md: sidebarVisible ? "block" : "none" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: drawerWidth,
                WebkitFontSmoothing: "antialiased",
                MozOsxFontSmoothing: "grayscale",
                imageRendering: "crisp-edges",
                background:
                  "linear-gradient(180deg, #0f172a 0%, #1e293b 35%, #111827 100%) !important",
                backgroundColor: "#0f172a !important",
                transition: "transform 0.3s ease-in-out",
                position: "fixed",
                height: "100vh",
              },
            }}
            open={sidebarVisible}
            onMouseEnter={handleSidebarMouseEnter}
            onMouseLeave={handleSidebarMouseLeave}
          >
            {drawer}
          </Drawer>
        </Box>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            flexBasis: 0,
            minWidth: 0,
            p: isFullBleedPage
              ? { xs: 0.75, sm: 1, md: 1.25 }
              : { xs: 1.5, sm: 2, md: 3 },
            width: {
              xs: "100%",
              md: sidebarVisible ? `calc(100% - ${drawerWidth}px)` : "100%",
            },
            maxWidth: "100%",
            overflowX: "hidden",
            boxSizing: "border-box",
            ml: { md: sidebarVisible ? 0 : 0 },
            mt: { xs: 7, md: 8 },
            backgroundColor: "background.default",
            minHeight: "100vh",
            transition: "width 0.3s ease-in-out, margin-left 0.3s ease-in-out",
            position: "relative",
          }}
        >
          {isApprovalLocked && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 6,
                borderRadius: 2,
                backdropFilter: "blur(7px)",
                background: "rgba(15, 23, 42, 0.35)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 3,
              }}
            >
              <Paper
                elevation={20}
                sx={{
                  maxWidth: 560,
                  width: "100%",
                  p: 4,
                  textAlign: "center",
                  borderRadius: 4,
                  background: "rgba(255,255,255,0.92)",
                }}
              >
                <Stack spacing={2} alignItems="center">
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      background:
                        approvalStatus === "rejected"
                          ? "rgba(239,68,68,0.12)"
                          : "rgba(37,99,235,0.12)",
                      color:
                        approvalStatus === "rejected" ? "#dc2626" : "#2563eb",
                      fontSize: 34,
                    }}
                  >
                    {approvalStatus === "rejected" ? "✕" : "⏳"}
                  </Box>
                  <Typography
                    variant="h4"
                    fontWeight={800}
                    color="text.primary"
                  >
                    {approvalLoading
                      ? "Checking approval..."
                      : approvalStatus === "rejected"
                        ? "Account rejected"
                        : "Waiting for approval"}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {approvalLoading
                      ? "We are refreshing your approval status."
                      : approvalStatus === "rejected"
                        ? "Your request was rejected. Please contact the app admin for details."
                        : "Your dashboard is ready, but access is locked until the app admin approves your business account."}
                  </Typography>
                  {approvalStatus === "rejected" && (
                    <Button
                      variant="contained"
                      color="error"
                      onClick={handleLogout}
                    >
                      Logout
                    </Button>
                  )}
                  {approvalStatus === "pending" &&
                    registrationFeeDue &&
                    canPayRegistrationFee &&
                    registrationTotalPaise > 0 && (
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={regPaymentBusy}
                        onClick={() => void handlePayRegistrationFee()}
                      >
                        {regPaymentBusy
                          ? "Starting payment…"
                          : `Pay registration fee (₹${(registrationTotalPaise / 100).toFixed(2)})`}
                      </Button>
                    )}
                  {regPaymentError && (
                    <Alert severity="error" sx={{ width: "100%" }}>
                      {regPaymentError}
                    </Alert>
                  )}
                </Stack>
              </Paper>
            </Box>
          )}
          {children}
        </Box>
      </Box>
    </>
  );
};

export default DashboardLayout;
